import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  color: ${theme.colors.text.primary};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.background.secondary};
  border-top: 4px solid ${theme.colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  margin: 0;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ErrorTitle = styled.h2`
  color: ${theme.colors.error.main};
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.text.secondary};
  margin-bottom: 2rem;
`;

const ActionButton = styled.button`
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary.dark};
    transform: translateY(-2px);
  }
`;

const DefaultDashboardLoader = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDefaultDashboard = async () => {
      if (authLoading) {
        return;
      }

      if (!isAuthenticated || !token) {
        navigate('/login');
        return;
      }

      const pickDefaultFromList = async () => {
        const listResponse = await api.get('/api/dashboards');
        const dashboards = Array.isArray(listResponse.data) ? listResponse.data : [];
        return dashboards.find((d) => d.is_default) || dashboards[0] || null;
      };

      const fetchWithRetry = async (url, { retries = 3 } = {}) => {
        let lastErr;
        for (let attempt = 0; attempt <= retries; attempt++) {
          try {
            return await api.get(url);
          } catch (err) {
            lastErr = err;
            const status = err.response?.status;
            if (![503, 502, 504].includes(status) || attempt === retries) throw err;
            await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          }
        }
        throw lastErr;
      };

      try {
        setLoading(true);
        setError(null);

        let defaultDashboard = null;

        try {
          const response = await fetchWithRetry('/api/dashboards/default');
          defaultDashboard = response.data;
        } catch (defaultErr) {
          const status = defaultErr.response?.status;

          if (status === 404) {
            navigate('/dashboards');
            return;
          }

          if ([401, 403].includes(status)) {
            navigate('/login');
            return;
          }

          // Fallback when /default fails (500/503/timeout) — list dashboards instead
          console.warn('Default dashboard endpoint failed, falling back to list:', defaultErr?.message);
          defaultDashboard = await fetchWithRetry('/api/dashboards').then((r) => {
            const dashboards = Array.isArray(r.data) ? r.data : [];
            return dashboards.find((d) => d.is_default) || dashboards[0] || null;
          });
        }

        if (defaultDashboard?.id) {
          navigate(`/dashboard/${defaultDashboard.id}`, { replace: true });
        } else {
          navigate('/dashboards', { replace: true });
        }
      } catch (err) {
        console.error('Error loading default dashboard:', err);

        const status = err.response?.status;
        if (status === 404) {
          navigate('/dashboards', { replace: true });
          return;
        }
        if ([401, 403].includes(status)) {
          navigate('/login', { replace: true });
          return;
        }

        const detail =
          err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Unknown error';
        setError(`Failed to load default dashboard (${detail})`);
      } finally {
        setLoading(false);
      }
    };

    loadDefaultDashboard();
  }, [authLoading, isAuthenticated, token, navigate]);

  if (loading) {
    return (
      <LoaderContainer>
        <Spinner />
        <LoadingText>Loading your dashboard...</LoadingText>
      </LoaderContainer>
    );
  }

  if (error) {
    return (
      <LoaderContainer>
        <ErrorContainer>
          <ErrorTitle>Unable to load dashboard</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
          <ActionButton onClick={() => navigate('/dashboards')}>
            Go to Dashboards
          </ActionButton>
        </ErrorContainer>
      </LoaderContainer>
    );
  }

  return null;
};

export default DefaultDashboardLoader;
